import styled from 'styled-components/macro';
import { FormattedMessage } from 'react-intl';

import {
  Typography,
  FormLabel,
  FormHelperText,
  FormControl,
  FormControlProps
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import HiTooltip from '../HiTooltip';
import { BaseReactComponent } from '../../../types';

export interface HiFormControlProps extends BaseReactComponent {
  /**
   * The content of the component.
   */
  children?: FormControlProps['children'];
  /**
   * The field label.
   */
  label?: string;
  /**
   * If `true`, the field is set as required.
   * @default true
   */
  required?: FormControlProps['required'];
  /**
   * If `true`, the label and helpers are displayed in disabled font, and the field inside is in a disabled state.
   * @default false
   */
  disabled?: FormControlProps['disabled'];
  /**
   * If `true`, the label is displayed in an error state.
   * @default false
   */
  error?: FormControlProps['error'];
  /**
   * If `error` is `true`, this text is displayed under the field.
   */
  errorText?: string;
  /**
   * The helper text displayed under the field.
   */
  helperText?: string;
  /**
   * A longer text displayed in a tooltip to share more information.
   */
  infoText?: string;
  /**
   * The `id` of the field to link the label with.
   */
  htmlFor?: string;
}

/**
 * HiFormControl is used in combination with an input (text fields, radios, checkboxes, selects, ...). It **provides all the field context** (label, helperText, error, tooltip).
 *
 * Additionally, it provides context such as filled/focused/error/required for form inputs. Relying on the context provides high flexibility and ensures that the state always stays consistent across the children of the `HiFormControl`.
 *
 * - Always add `HiFormControl` component **around your inputs** inside a form.
 * - In a form, lay out your HiFormControls vertically to create a one column form (There are a few exceptions, but it should be validated by a Designer, (e.g. Day/Month/Year)).
 * - A `24px margin` is applied automatically between every HiFormControl.
 * - The HiFormControl has a max-width to 400px.
 */
const HiFormControl = ({
  children,
  label,
  required = true,
  error = false,
  errorText,
  helperText,
  infoText,
  htmlFor,
  ...other
}: HiFormControlProps) => {
  return (
    <StyledFormControl
      {...other}
      variant="standard"
      margin="normal"
      error={error}
    >
      {label && (
        <div className="HiFormControl-label-wrapper">
          <FormLabel
            htmlFor={htmlFor}
            sx={{ typography: 'body2', color: 'text.secondary' }}
            classes={{
              error: 'HiFormControl-error',
              focused: 'HiFormControl-focused'
            }}
          >
            {label}
          </FormLabel>

          {infoText && (
            <HiTooltip describeChild arrow placement="top" title={infoText}>
              <InfoOutlined className="HiFormControl-info-icon" color="info" />
            </HiTooltip>
          )}

          {!required && (
            <Typography variant="caption" className="HiFormControl-optional">
              <FormattedMessage id="HiFormControl.optional" />
            </Typography>
          )}
        </div>
      )}

      {children}

      {(!error || !errorText) && helperText && (
        <FormHelperText className="HiFormControl-bottomText">
          {helperText}
        </FormHelperText>
      )}
      {error && errorText && (
        <FormHelperText className="HiFormControl-bottomText">
          {errorText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

const StyledFormControl = styled(FormControl)`
  max-width: 400px;

  .HiFormControl-label-wrapper {
    display: flex;
    align-items: baseline;
    margin-bottom: 2px;

    .HiFormControl-optional {
      margin-left: 8px;
    }

    .HiFormControl-error.HiFormControl-focused {
      color: ${({ theme }) => theme.palette.primary.main};
    }

    .HiFormControl-info-icon {
      margin-left: 4px;
      font-size: 18px;

      cursor: pointer;
      align-self: center;
    }
  }

  .HiFormControl-bottomText {
    margin-top: 4px;
  }
`;

export default HiFormControl;
